/**
 * ⭐ BROWSER SHIM for `node:crypto`, referenced only by this package's `browser` field.
 *
 * # Why this package needs one at all
 *
 * `src/desired-state.ts` imports `createHash` from `node:crypto` at module scope, for `versionSlug()`'s
 * 4-hex-char disambiguating suffix. The barrel (`src/index.ts`) re-exports `versionSlug`, so **any
 * browser consumer that value-imports the barrel pulls a Node builtin into the browser** — even though
 * nothing in a browser ever calls it.
 *
 * Bundlers map an unmappable Node builtin to a stub that fails. Measured 2026-08-06, three of this
 * repo's SPAs consume the barrel as a value and **two rendered a completely blank page** from
 * `pnpm dev`:
 *
 *   · `cp-admin-ui`   — `SyntaxError: … does not provide an export named 'createHash'` (ESM LINK time)
 *   · `site-admin-ui` — `Cannot access "node:crypto.createHash" in client code` (property-ACCESS time)
 *   · `dp/shell`      — same as site-admin-ui
 *
 * ⚠️ **Two different symptoms, one cause.** Anyone grepping logs for the link-time `SyntaxError` would
 * conclude the other two are a different bug. Both end with `#root` empty.
 *
 * ⛔ **The failure shape is why nobody noticed for months: a blank page with a silent terminal reads as
 * "the application is broken", not "the tooling is broken".**
 *
 * # ⚠️ THIS FIELD IS HONOURED IN PRODUCTION BUILDS TOO — measured, not assumed
 *
 * ⛔ Unlike a bundler alias gated to `command === 'serve'`, `package.json`'s `browser` map is honoured by
 * Vite/Rollup/webpack/esbuild during a **production build**. ⇒ The earlier per-package fix was safe
 * *structurally* (withheld from `build`, so the production path was untouched by construction). **That
 * guarantee is gone here and is replaced by tree-shaking**, so it has to be measured on the artefact.
 *
 * ✅ **Measured 2026-08-06 on all three affected SPAs, AFTER this field existed**, over every emitted
 * `.js` file, with BOUNDED needles:
 *
 * | bundle | files | `node:crypto` | `createHash` | `browser-shims` | shim text |
 * |---|---|---|---|---|---|
 * | `cp-admin-ui`   | 77 | 0 | 0 | 0 | 0 |
 * | `site-admin-ui` | 53 | 0 | 0 | 0 | 0 |
 * | `dp/shell`      | 10 | 0 | 0 | 0 | 0 |
 *
 * Positive control `versionSlug`: 33 / 15 / 11 — non-zero, so the zeros are a measurement rather than a
 * sweep that read nothing. ⇒ **`versionSlug` is tree-shaken out of all three production bundles and this
 * shim reaches none of them.** It remains a local-development fix in effect, but by tree-shaking rather
 * than by construction — and that distinction is why the table above is dated and scoped.
 *
 * ⛔ **Re-measure this table if the barrel's browser consumers change**, and use a BOUNDED needle:
 * a bare `createHash` matches react-router's `createHashHistory`/`createHashRouter`, which produced
 * **7 false hits in `dp/shell`** and briefly read as this shim shipping to production.
 *
 * # Why the `browser` field, and not any of the alternatives
 *
 * ⭐ **It changes NO algorithm, so there is no digest risk at all.** `versionSlug`'s output is a NAMING
 * PRIMITIVE — it feeds HTTPRoute keys and per-version resource identity across the operator and the
 * `zion-dev` launcher (`instance-registry`, `fixture-spawner`, `env-builder`'s
 * `ZION_INSTANCE_VERSION_SLUG`, `core-api-emulator`). ⛔ **If the digest changed by one byte, every
 * deployed data plane would rename its routed resources, and the operator — a reconciler — would
 * create-new/delete-stale to converge: a live fleet-wide rollout triggered by what looks like a
 * build-tooling cleanup.** Node consumers resolve the real `node:crypto` and are untouched by this file,
 * so that hazard is **structurally absent** rather than tested-for.
 *
 * Rejected alternatives, for the record:
 *   · **A `./desired-state` subpath export** — does not help. The offending import is
 *     the old console's branding module → *this barrel*, and that package was read-only, so
 *     the import that pulls the barrel cannot be changed.
 *   · **A pure-JS SHA-256** — viable only with byte-identical output proven over a corpus. Strictly more
 *     risk for the same benefit, once the `browser` field was measured to work.
 *   · **A per-consumer bundler alias** — needs one per SPA (three today, five sites more), and each is a
 *     place to forget. This fixes it once, at the source.
 *   · **A deferred/dynamic `import()`** — forbidden by CLAUDE.md outside genuinely optional deps.
 *
 * # ⛔ IT THROWS. DO NOT "IMPROVE" IT INTO SOMETHING THAT RETURNS A VALUE
 *
 * The obvious stub returns a fake digest. That would be **worse than the crash it replaces**: a constant
 * digest would silently collide two different long versions onto ONE Kubernetes object name — a wrong
 * answer that looks right, in the browser only, diverging from every server. Throwing is also what the
 * bundler's own shim does on property access, so this keeps dev behaviour honest: fine if nothing calls
 * it (nothing does), loud the moment something starts.
 *
 * ⇒ If this ever throws, the fix is to stop pulling a Node-only path into the browser — not to make the
 * stub lie. `src/__tests__/browser-shim.test.ts` pins both halves: that Node still computes a real,
 * byte-identical digest, and that this shim throws.
 *
 * ⚠️ Plain `.js` at the package root, deliberately: `files` publishes only `dist`, and the build
 * (`preserveModules`) emits only modules reachable from the entry graph — nothing imports this, so a
 * `src/` version would never be emitted. It is listed in `files` so it publishes.
 */

const REASON =
  'node:crypto is not available in the browser. This shim exists so a browser consumer of ' +
  '@zion/sdk-cp-dp-contracts can load the barrel, which re-exports versionSlug() from desired-state. ' +
  'Nothing in a browser should call it — versionSlug is a Kubernetes-naming primitive. If you are ' +
  'seeing this, a Node-only code path became reachable from UI code; fix that, not this shim.';

/** ⛔ Throws. Present only so the specifier resolves and the barrel can link. */
export function createHash() {
  throw new Error(`createHash: ${REASON}`);
}

/** ⛔ Throws, same reasoning. Present because this package's Node code may add it. */
export function randomUUID() {
  throw new Error(`randomUUID: ${REASON}`);
}

export default { createHash, randomUUID };
